import type { AuditRecord, ImportedAudit } from "./types";

const aliases = {
  date: ["дата звонка", "дата события", "дата"],
  manager: ["ответственный", "менеджер"],
  counterparty: ["контрагент", "клиент"],
  department: ["отдел"],
  comment: ["комментарий", "комментарий к звонку"],
  status: ["результат", "результат звонка", "статус"],
  eventType: ["вид события", "тип события"],
} as const;

type Field = keyof typeof aliases;
type ColumnMap = Partial<Record<Field, number>>;

const labels: Record<Field, string> = {
  date: "Дата звонка",
  manager: "Менеджер",
  counterparty: "Контрагент",
  department: "Отдел",
  comment: "Комментарий",
  status: "Результат звонка",
  eventType: "Вид события",
};

const required: Field[] = ["comment", "status", "eventType"];

function resolveColumns(headers: unknown[]): ColumnMap {
  const normalized = headers.map(window.CallAudit.normalize);
  const columns: ColumnMap = {};
  (Object.keys(aliases) as Field[]).forEach((field) => {
    const index = aliases[field].findIndex((alias) => normalized.includes(alias));
    if (index !== -1) columns[field] = normalized.indexOf(aliases[field][index]);
  });
  const missing = required.filter((field) => columns[field] == null);
  if (missing.length) {
    throw new Error(`Не найдены обязательные столбцы: ${missing.map((field) => labels[field]).join(", ")}.`);
  }
  return columns;
}

function cell(row: unknown[], columns: ColumnMap, field: Field): unknown {
  const index = columns[field];
  return index == null || index >= row.length || row[index] == null ? "" : row[index];
}

export async function importExcel(file: File): Promise<ImportedAudit> {
  if (!window.XLSX || !window.CallAudit) throw new Error("Локальные модули анализа не загрузились. Обновите страницу.");
  const workbook = window.XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true, dense: true });
  if (!workbook.SheetNames.length) throw new Error("В книге нет листов.");
  const sheetName = workbook.SheetNames[0];
  const rows = window.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    raw: true,
    defval: "",
    blankrows: false,
  }) as unknown[][];
  if (!rows.length) throw new Error("Файл не содержит строк.");

  const columns = resolveColumns(rows[0]);
  const warnings = (["date", "manager", "counterparty", "department"] as Field[])
    .filter((field) => columns[field] == null)
    .map((field) => `Не найден необязательный столбец: ${labels[field]}`);
  const records: AuditRecord[] = [];
  rows.slice(1).forEach((row, index) => {
    if (!row.some((value) => value != null && String(value).trim())) return;
    records.push({
      sourceRow: index + 2,
      date: cell(row, columns, "date"),
      manager: String(cell(row, columns, "manager") || "").trim(),
      counterparty: String(cell(row, columns, "counterparty") || "").trim(),
      department: String(cell(row, columns, "department") || "").trim(),
      comment: String(cell(row, columns, "comment") || "").trim(),
      status: String(cell(row, columns, "status") || "").trim(),
      eventType: String(cell(row, columns, "eventType") || "").trim(),
    });
  });
  if (!records.length) throw new Error("После строки заголовков не найдено событий для проверки.");
  return { records, warnings, sheetName };
}
